import {
  Box,
  Button,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Clock, Eye, MessageSquare } from "lucide-react";
import { useState } from "react";
import { format, parseISO } from "date-fns";
import { getWhatsappConversation } from "@/modules/lead-page/api/lead-page.v2.api";
import {
  ActivityItem,
  WhatsappSenderRole,
} from "@/modules/lead-page/dtos/index-v2";
import CommentBulb from "./comment-bulb";
import {
  commentBulbBackgroundMap,
  commentBulbColorMap,
} from "@/modules/lead-page/new/constants";

interface WhatsappConversationInfoProps {
  item: ActivityItem;
  onViewConversation: () => void;
}

interface ParsedWhatsappContent {
  summary?: string;
  shortSummary?: string;
  msgCount?: number;
  duration?: string;
}

const parseContent = (content: string | null): ParsedWhatsappContent | null => {
  if (!content) return null;
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed === "object" && parsed !== null) {
      return parsed as ParsedWhatsappContent;
    }
  } catch {
    // Not JSON — treat as plain text summary
    return { summary: content };
  }
  return null;
};

const WA_BUBBLE_AGENT = "#DCF8C6";
const WA_BUBBLE_LEAD = "#FFFFFF";
const WA_WALL = "#ECE5DD";

const WhatsappConversationInfo = ({
  item,
  onViewConversation,
}: WhatsappConversationInfoProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const parsed = parseContent(item.content);

  // Fetch conversation messages for preview when expanded
  const { data: conversationPage } = useQuery(
    ["whatsappConversationPreview", item.uid],
    () => getWhatsappConversation(String(item.uid)),
    {
      enabled: expanded,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const summary = parsed?.summary ?? item.content;
  const shortSummary = parsed?.shortSummary;
  const msgCount = parsed?.msgCount;
  const duration = parsed?.duration;
  const previewMessages = conversationPage?.messages?.slice(-3);

  const hasSummary = summary && summary.trim().length > 0;
  const hasMetadata = msgCount || duration;

  return (
    <Stack spacing={theme.nawy.spacing.xs}>
      {/* Metadata chips */}
      {hasMetadata && (
        <Stack
          direction="row"
          spacing={theme.nawy.spacing.sm}
          alignItems="center"
        >
          {msgCount && (
            <Stack
              direction="row"
              spacing="4px"
              alignItems="center"
              sx={{
                px: theme.nawy.spacing.xs,
                py: "2px",
                borderRadius: theme.nawy.radius.sm,
                background: theme.nawy.colors.semantic.indicator.greenLight,
              }}
            >
              <MessageSquare
                size={11}
                color={theme.nawy.colors.semantic.indicator.green}
              />
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.xs.regular,
                  fontWeight: 600,
                  color: theme.nawy.colors.semantic.indicator.green,
                }}
              >
                {msgCount} msgs
              </Typography>
            </Stack>
          )}
          {duration && (
            <Stack
              direction="row"
              spacing="4px"
              alignItems="center"
              sx={{
                px: theme.nawy.spacing.xs,
                py: "2px",
                borderRadius: theme.nawy.radius.sm,
                background: theme.nawy.colors.semantic.layer.secondary,
              }}
            >
              <Clock
                size={11}
                color={theme.nawy.colors.semantic.text.tertiary}
              />
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.xs.regular,
                  fontWeight: 500,
                  color: theme.nawy.colors.semantic.text.tertiary,
                }}
              >
                {duration}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      {/* Summary section */}
      {hasSummary && (
        <>
          {/* Short summary (always visible) or expandable full summary */}
          {shortSummary && !expanded ? (
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.secondary,
                lineHeight: 1.5,
              }}
            >
              {shortSummary}
            </Typography>
          ) : (
            <CommentBulb
              content={summary!}
              backgroundColor={commentBulbBackgroundMap.get("default")}
              color={commentBulbColorMap.get("default")}
              mentions={item.mentions}
            />
          )}

          {/* Expand/collapse toggle for message preview */}
          {(shortSummary || previewMessages) && (
            <Button
              size="small"
              onClick={() => setExpanded((prev) => !prev)}
              endIcon={
                <ChevronDown
                  size={14}
                  style={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              }
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                ...theme.nawy.typography.english.variants.body.sm.semibold,
                color: theme.nawy.colors.semantic.indicator.green,
                px: theme.nawy.spacing.xs,
                py: "2px",
                borderRadius: theme.nawy.radius.sm,
                "&:hover": {
                  background: theme.nawy.colors.semantic.indicator.greenLight,
                },
              }}
            >
              {expanded ? "Show less" : "Show details"}
            </Button>
          )}
        </>
      )}

      {/* Expanded content: message preview */}
      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={theme.nawy.spacing.sm}>
          {/* Message preview */}
          {previewMessages && previewMessages.length > 0 && (
            <Box
              sx={{
                background: WA_WALL,
                borderRadius: theme.nawy.radius.md,
                p: theme.nawy.spacing.sm,
              }}
            >
              <Stack spacing="4px">
                {previewMessages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{
                      display: "flex",
                      justifyContent:
                        msg.senderRole === WhatsappSenderRole.Agent
                          ? "flex-end"
                          : "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "80%",
                        px: theme.nawy.spacing.xs,
                        py: "4px",
                        borderRadius: theme.nawy.radius.md,
                        background:
                          msg.senderRole === WhatsappSenderRole.Agent
                            ? WA_BUBBLE_AGENT
                            : WA_BUBBLE_LEAD,
                        border:
                          msg.senderRole === WhatsappSenderRole.Agent
                            ? "none"
                            : `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
                        boxShadow: "0 1px 0 rgba(0,0,0,0.05)",
                      }}
                    >
                      <Typography
                        sx={{
                          ...theme.nawy.typography.english.variants.body.xs
                            .regular,
                          color: theme.nawy.colors.semantic.text.primary,
                          lineHeight: 1.4,
                          wordBreak: "break-word",
                        }}
                      >
                        {msg.message}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "9px",
                            color: theme.nawy.colors.semantic.text.tertiary,
                            ml: "6px",
                            verticalAlign: "baseline",
                          }}
                        >
                          {format(parseISO(msg.time), "h:mm a")}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
              {msgCount && msgCount > 3 && (
                <Typography
                  sx={{
                    textAlign: "center",
                    ...theme.nawy.typography.english.variants.body.xs.regular,
                    color: theme.nawy.colors.semantic.text.tertiary,
                    fontStyle: "italic",
                    mt: theme.nawy.spacing.xs,
                  }}
                >
                  +{msgCount - 3} more messages
                </Typography>
              )}
            </Box>
          )}

          {/* View full conversation button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                onViewConversation();
              }}
              startIcon={<Eye size={14} />}
              sx={{
                textTransform: "none",
                borderRadius: theme.nawy.radius.sm,
                borderColor: theme.nawy.colors.semantic.indicator.green,
                color: theme.nawy.colors.semantic.indicator.green,
                ...theme.nawy.typography.english.variants.body.sm.semibold,
                height: 28,
                px: theme.nawy.spacing.sm,
                "&:hover": {
                  borderColor: theme.nawy.colors.semantic.indicator.green,
                  background: theme.nawy.colors.semantic.indicator.greenLight,
                },
              }}
            >
              View detailed conversation
            </Button>
          </Box>
        </Stack>
      </Collapse>

      {/* View conversation button when not expanded */}
      {!expanded && (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onViewConversation();
          }}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none",
            ...theme.nawy.typography.english.variants.body.sm.semibold,
            color: theme.nawy.colors.semantic.text.accent,
            px: 0,
            minWidth: "auto",
            "&:hover": {
              background: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          View detailed conversation →
        </Button>
      )}
    </Stack>
  );
};

export default WhatsappConversationInfo;
